import { FormEvent, useEffect, useState } from "react";
import {
  ActivityEvent,
  ActivitySuggestion,
  ActivityUsageReport,
  Device,
  api,
  session,
} from "./api";
import { duration, localInput } from "./format";
import { ListControls, usePagedList } from "./Pagination";
import { FilterBar, PageHeader } from "./ui";
import { UsageTimelineChart, usageColors } from "./UsageTimelineChart";

export const categoryLabels: Record<string, string> = {
  productive: "Produtivo",
  neutral: "Neutro",
  unproductive: "Improdutivo",
  unclassified: "Sem classificação",
};

// Mesma normalização do servidor: sem extensão e sem diferenciar maiúsculas.
export const normalizeProcess = (value: string) => value.trim().replace(/\.exe$/i, "").toLowerCase();

export const suggestionKindLabel = (kind: ActivitySuggestion["kind"]) =>
  kind === "device" ? "Equipamento" : kind === "user" ? "Usuário" : "Processo";

export function ActivityPage() {
  const [query, setQuery] = useState("");
  const [from, setFrom] = useState(localInput(new Date(Date.now() - 86400000)));
  const [to, setTo] = useState(localInput(new Date()));
  const [user, setUser] = useState("");
  const [process, setProcess] = useState("");
  const [deviceQuery, setDeviceQuery] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [devices, setDevices] = useState<Device[]>([]);
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [report, setReport] = useState<ActivityUsageReport>();
  const [reportFilters, setReportFilters] = useState<{ from: string; to: string; deviceId?: string }>();
  const [suggestions, setSuggestions] = useState<ActivitySuggestion[]>([]);
  const [suggestionIndex, setSuggestionIndex] = useState(-1);
  const [queryOpen, setQueryOpen] = useState(false);
  const [deviceOpen, setDeviceOpen] = useState(false);
  const pagedActivity = usePagedList(events.filter((event) => event.eventType !== "window_list"));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [rules, setRules] = useState<Array<{ pattern: string; kind: string }>>([]);
  const [goalMessage, setGoalMessage] = useState("");
  const [goalBusy, setGoalBusy] = useState(false);
  const canManageGoals = session.user()?.role === "owner" || session.user()?.role === "admin";

  useEffect(() => {
    api.devices().then(setDevices).catch(() => undefined);
  }, []);

  useEffect(() => {
    api
      .activityCategories()
      .then((page) => setRules(page.rules))
      .catch(() => undefined);
  }, []);

  // Sugestões do campo em linguagem natural: pequeno atraso para não consultar o
  // servidor a cada tecla.
  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      api
        .activitySuggestions(term)
        .then((page) => {
          if (cancelled) return;
          setSuggestions(page.items);
          setSuggestionIndex(-1);
        })
        .catch(() => {
          if (!cancelled) setSuggestions([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query]);

  const normalizedDevice = deviceQuery.trim().toLowerCase();
  const deviceMatches = (normalizedDevice
    ? devices.filter((item) => item.machineName.toLowerCase().includes(normalizedDevice))
    : devices
  ).slice(0, 8);

  const pickSuggestion = (suggestion: ActivitySuggestion) => {
    // A sugestão vira uma frase que o interpretador já entende; assim o operador pode
    // continuar digitando "ontem às 10h" depois de escolher o processo.
    setQuery(
      suggestion.kind === "device"
        ? suggestion.value
        : `${suggestion.kind === "user" ? "usuário" : "processo"} ${suggestion.value}`,
    );
    setSuggestions([]);
    setSuggestionIndex(-1);
    setQueryOpen(false);
  };

  const pickDevice = (device: Device) => {
    setDeviceId(device.id);
    setDeviceQuery(device.machineName);
    setDeviceOpen(false);
  };

  const search = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setQueryOpen(false);
    setDeviceOpen(false);
    try {
      let filters: { from: string; to: string; user?: string; process?: string; deviceId?: string; eventType?: string } = {
        from: new Date(from).toISOString(),
        to: new Date(to).toISOString(),
        user: user || undefined,
        process: process || undefined,
        deviceId: deviceId || undefined,
      };
      if (query.trim()) {
        const parsed = await api.interpretActivity(query);
        filters = {
          from: parsed.from,
          to: parsed.to,
          user: parsed.user || filters.user,
          process: parsed.process || filters.process,
          deviceId: parsed.deviceId ?? filters.deviceId,
          eventType: parsed.eventType,
        };
        setFrom(localInput(new Date(parsed.from)));
        setTo(localInput(new Date(parsed.to)));
        setUser(filters.user ?? "");
        setProcess(filters.process ?? "");
        if (parsed.deviceId) {
          const match = devices.find((item) => item.id === parsed.deviceId);
          setDeviceId(parsed.deviceId);
          setDeviceQuery(match?.machineName ?? "");
        }
      }
      setEvents((await api.searchActivity(filters)).items);
      const applied = { from: filters.from, to: filters.to, deviceId: filters.deviceId };
      setReportFilters(applied);
      setReport(await api.activityReport(applied));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha na pesquisa.");
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = async () => {
    if (!reportFilters) return;
    setBusy(true);
    setError("");
    try {
      const blob = await api.exportActivityReport(reportFilters);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `produtividade-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      // Revogar no próximo tick evita interromper o download em navegadores que
      // ainda estão consumindo o blob (Safari/Firefox).
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao exportar o relatório.");
    } finally {
      setBusy(false);
    }
  };

  const goalFor = (process: string) =>
    rules.find((rule) => rule.pattern === normalizeProcess(process))?.kind ?? "unclassified";

  const setGoal = (process: string, kind: string) => {
    const pattern = normalizeProcess(process);
    setRules((current) => {
      const rest = current.filter((rule) => rule.pattern !== pattern);
      return kind === "unclassified" ? rest : [...rest, { pattern, kind }];
    });
    setGoalMessage("");
  };

  const saveGoals = async () => {
    setGoalBusy(true);
    setError("");
    setGoalMessage("");
    try {
      const saved = await api.saveActivityCategories(rules);
      setRules(saved.rules);
      setGoalMessage("Metas salvas. Os totais por categoria foram recalculados.");
      if (reportFilters) setReport(await api.activityReport(reportFilters));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao salvar as metas.");
    } finally {
      setGoalBusy(false);
    }
  };

  const periodLabel = reportFilters
    ? `${new Date(reportFilters.from).toLocaleDateString("pt-BR")} – ${new Date(reportFilters.to).toLocaleDateString("pt-BR")}`
    : "";

  return (
    <>
      <PageHeader
        eyebrow="ATIVIDADE DO USUÁRIO"
        title="Atividade do usuário"
        subtitle="Localize quem, quando e onde; abra a evidência temporal correspondente."
      />
      <FilterBar onSubmit={search} actions={<button disabled={busy}>{busy ? "Pesquisando…" : "Pesquisar"}</button>}>
        <label className="natural-search suggestion-field">
          Pesquisa em linguagem natural
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setQueryOpen(true);
            }}
            onFocus={() => setQueryOpen(true)}
            onBlur={() => window.setTimeout(() => setQueryOpen(false), 150)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setSuggestions([]);
                return;
              }
              if (!suggestions.length) return;
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setSuggestionIndex((index) => (index + 1) % suggestions.length);
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setSuggestionIndex((index) => (index <= 0 ? suggestions.length - 1 : index - 1));
              } else if (e.key === "Enter" && suggestionIndex >= 0) {
                e.preventDefault();
                pickSuggestion(suggestions[suggestionIndex]);
              }
            }}
            placeholder="Quem abriu o software X ontem às 10h?"
            autoComplete="off"
          />
          {queryOpen && suggestions.length > 0 && (
            <ul className="suggestions" role="listbox">
              {suggestions.map((item, index) => (
                <li key={`${item.kind}-${item.value}`}>
                  <button
                    type="button"
                    className={index === suggestionIndex ? "active" : ""}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pickSuggestion(item);
                    }}
                  >
                    <span>{item.value}</span>
                    <small>{suggestionKindLabel(item.kind)}</small>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </label>
        <label className="suggestion-field">
          Equipamento
          <input
            value={deviceQuery}
            onChange={(e) => {
              setDeviceQuery(e.target.value);
              setDeviceId("");
              setDeviceOpen(true);
            }}
            onFocus={() => setDeviceOpen(true)}
            onBlur={() => window.setTimeout(() => setDeviceOpen(false), 150)}
            placeholder="Todos os equipamentos"
            autoComplete="off"
          />
          {deviceOpen && (
            <ul className="suggestions" role="listbox">
              <li>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setDeviceId("");
                    setDeviceQuery("");
                    setDeviceOpen(false);
                  }}
                >
                  <span>Todos os equipamentos</span>
                  <small>sem filtro</small>
                </button>
              </li>
              {deviceMatches.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pickDevice(item);
                    }}
                  >
                    <span>{item.machineName}</span>
                    <small>{item.status === "online" ? "online" : "offline"}</small>
                  </button>
                </li>
              ))}
              {!deviceMatches.length && (
                <li>
                  <button type="button" disabled>
                    <span>Nenhum equipamento encontrado</span>
                  </button>
                </li>
              )}
            </ul>
          )}
        </label>
        <label>
          De
          <input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          Até
          <input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label>
          Usuário
          <input value={user} onChange={(e) => setUser(e.target.value)} autoComplete="off" />
        </label>
        <label>
          Processo
          <input value={process} onChange={(e) => setProcess(e.target.value)} autoComplete="off" />
        </label>
      </FilterBar>
      {error && <p className="error">{error}</p>}
      {report && (
        <section className="panel usage-report">
          <div className="panel-title">
            <h2>Tempo por programa</h2>
            <span>
              {report.sampleCount} amostras{periodLabel ? ` · ${periodLabel}` : ""}
            </span>
          </div>
          <div className="print-only">Relatório de produtividade{periodLabel ? ` · ${periodLabel}` : ""}</div>
          <div className="usage-cards">
            <div>
              <span>Tempo ativo</span>
              <strong>{duration(report.activeSeconds)}</strong>
            </div>
            <div>
              <span>Tempo ocioso</span>
              <strong>{duration(report.idleSeconds)}</strong>
            </div>
            <div className="usage-actions">
              <button type="button" className="small-button" disabled={busy} onClick={exportCsv}>
                Exportar CSV (Excel)
              </button>
              <button type="button" className="small-button" onClick={() => window.print()}>
                Imprimir / PDF
              </button>
            </div>
          </div>
          {report.categories && report.categories.length > 0 && (
            <div className="usage-categories">
              {report.categories.map((item) => (
                <div key={item.kind} className={`usage-category ${item.kind}`}>
                  <span>{categoryLabels[item.kind] ?? item.kind}</span>
                  <strong>{duration(item.seconds)}</strong>
                  <small>{Math.round(item.share * 100)}% do tempo ativo</small>
                </div>
              ))}
            </div>
          )}
          {report.processes.length ? (
            <div className="usage-chart">
              <div className="usage-scale">
                <span>0%</span>
                <span>25%</span>
                <span>50%</span>
                <span>75%</span>
                <span>100%</span>
              </div>
              {report.processes.map((item, index) => (
                <div className="usage-row" key={item.process}>
                  <span title={item.process}>{item.process}</span>
                  <div className="usage-track">
                    <i
                      style={{
                        width: `${Math.max(1.5, item.share * 100)}%`,
                        background: usageColors[index % usageColors.length],
                      }}
                    />
                  </div>
                  <b>
                    {duration(item.seconds)} · {Math.round(item.share * 100)}%
                  </b>
                </div>
              ))}
            </div>
          ) : (
            <p className="muted usage-empty">
              Sem atividade suficiente no período. O companion precisa estar em execução para registrar trocas de
              janela.
            </p>
          )}
          <UsageTimelineChart
            timeline={report.timeline}
            processOrder={report.processes.map((item) => item.process)}
          />
          {report.timeline?.byDevice && report.timeline.byDevice.length > 1 && (
            <details className="usage-devices">
              <summary>Por equipamento ({report.timeline.byDevice.length})</summary>
              {report.timeline.byDevice.map((device) => (
                <UsageTimelineChart
                  key={device.deviceId}
                  timeline={report.timeline}
                  processOrder={report.processes.map((item) => item.process)}
                  buckets={device.buckets}
                  title={device.machineName}
                />
              ))}
            </details>
          )}
          {canManageGoals && report.processes.length > 0 && (
            <div className="usage-goals">
              <div className="panel-title">
                <h3>Metas de produtividade</h3>
                <span>classifique os programas mais usados</span>
              </div>
              {report.processes.map((item) => (
                <label key={item.process}>
                  <span title={item.process}>{item.process}</span>
                  <select
                    value={goalFor(item.process)}
                    onChange={(event) => setGoal(item.process, event.target.value)}
                  >
                    <option value="unclassified">Sem classificação</option>
                    <option value="productive">Produtivo</option>
                    <option value="neutral">Neutro</option>
                    <option value="unproductive">Improdutivo</option>
                  </select>
                </label>
              ))}
              <div className="usage-goals-actions">
                <button type="button" className="small-button" disabled={goalBusy} onClick={saveGoals}>
                  {goalBusy ? "Salvando…" : "Salvar metas"}
                </button>
                {goalMessage && <small>{goalMessage}</small>}
              </div>
            </div>
          )}
        </section>
      )}
      <section className="panel">
        <div className="panel-title">
          <h2>Eventos encontrados</h2>
          <span>{pagedActivity.total} resultados</span>
        </div>
        <div className="activity-results">
          {pagedActivity.pageItems.map((item) => (
            <article key={item.id}>
              <div>
                <strong>
                  {item.machineName} · {item.accountName}
                </strong>
                <span>{item.eventType.replaceAll("_", " ")}</span>
              </div>
              <div>
                <b>{item.processName ?? "—"}</b>
                <small>{item.windowTitle ?? new Date(item.agentTimestamp).toLocaleString("pt-BR")}</small>
              </div>
              {item.nearestSnapshotId ? (
                <a href={`#/devices/${item.deviceId}/timeline?at=${encodeURIComponent(item.agentTimestamp)}`}>
                  Abrir evidência →
                </a>
              ) : (
                <small>Sem captura próxima</small>
              )}
            </article>
          ))}
          {!events.length && (
            <div className="empty">
              <b>Nenhum evento carregado</b>
              <span>Use a pesquisa acima.</span>
            </div>
          )}
        </div>
        <ListControls
          page={pagedActivity.page}
          pages={pagedActivity.pages}
          size={pagedActivity.size}
          total={pagedActivity.total}
          onSize={pagedActivity.changeSize}
          onPrev={pagedActivity.prev}
          onNext={pagedActivity.next}
        />
      </section>
    </>
  );
}

